import _ from 'lodash';
import { createDateFilterConfig } from 'src/components/filter/FilterDatePicker';
import { FILTER_OPERATORS } from 'src/contstant/FilterTypes';
import { FilterItemType } from 'src/hooks/useGenericFilter';
import { BusinessUnitWithDivisions } from 'src/types/orbit/BusinessUnit';
import { Division } from 'src/types/orbit/Division';
import { ExportFileConfig } from 'src/utils/exportFileUtils';
import { formatDateTime } from 'src/utils/generalUtils';
import { flatMap } from 'lodash';
export enum PropertyKey {
  BUSINESS_UNIT_ACTIVE_STATUS = 'parent.businessUnitisActiveStatus',
  BUSINESS_UNIT_LAST_UPDATED = 'parent.businessUnitLastUpdatedAt',
  BUSINESS_UNIT_CREATED = 'parent.businessUnitCreated',
  BUSINESS_UNIT = 'parent.businessUnit',
  DIVISION_LAST_UPDATED = 'child.divisionLastUpdatedAt',
  DIVISION_CREATED = 'child.divisionCreatedAt',
  DIVISION_ACTIVE_STATUS = 'child.divisionActiveStatus',
}

export const getItemValue = (
  item: BusinessUnitWithDivisions | Division,
  propertyKey: string,
): any => {
  if (!item) return '';

  switch (propertyKey) {
    case PropertyKey.BUSINESS_UNIT_ACTIVE_STATUS:
      return {
        itemValue: item.isActive,
        type: FilterItemType.BOOLEAN,
      };

    case PropertyKey.BUSINESS_UNIT_LAST_UPDATED:
      return {
        itemValue: item.lastUpdatedAt,
        type: FilterItemType.DATE,
      };
    case PropertyKey.BUSINESS_UNIT_CREATED:
      return {
        itemValue: item.createdAt,
        type: FilterItemType.DATE,
      };

    //division fields
    case PropertyKey.DIVISION_LAST_UPDATED:
      return {
        itemValue: item.lastUpdatedAt,
        type: FilterItemType.DATE,
      };
    case PropertyKey.DIVISION_CREATED:
      return {
        itemValue: item.createdAt,
        type: FilterItemType.DATE,
      };
    case PropertyKey.DIVISION_ACTIVE_STATUS: {
      return {
        itemValue: item.isActive,
        type: FilterItemType.BOOLEAN,
      };
    }
    // other cases...
    default:
      return { itemValue: '' };
  }
};

export const getSearchableFields = (item: BusinessUnitWithDivisions) => [
  item.businessUnitId || '',
  item.isActive || false,
  item.name || '',
  item.lastUpdatedAt || 0,
  item.lastUpdatedBy || '',
];

// Initial filter options
export const initialFilters = [
  {
    propertyKey: PropertyKey.BUSINESS_UNIT_ACTIVE_STATUS,
    value: 'true',
    label: 'Active',
  },
  {
    propertyKey: PropertyKey.BUSINESS_UNIT_ACTIVE_STATUS,
    value: 'false',
    label: 'Inactive',
  },
  {
    propertyKey: PropertyKey.DIVISION_ACTIVE_STATUS,
    value: 'true',
    label: 'Active',
  },
  {
    propertyKey: PropertyKey.DIVISION_ACTIVE_STATUS,
    value: 'false',
    label: 'Inactive',
  },
];

export const getTeamsFilterProperties = (isPlatformAdmin: boolean): any[] => {
  const baseFilters: any[] = [
    {
      key: PropertyKey.DIVISION_ACTIVE_STATUS,
      propertyLabel: 'Active Status (Division)',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Status',

      group: 'Division',
    },
    createDateFilterConfig({
      key: PropertyKey.DIVISION_LAST_UPDATED,
      propertyLabel: 'Updated At (Division)',
      groupValuesLabel: 'Date',
      group: 'Division',
    }),
    createDateFilterConfig({
      key: PropertyKey.DIVISION_CREATED,
      propertyLabel: 'Created At (Division)',
      groupValuesLabel: 'Date',
      group: 'Division',
    }),
  ];

  if (isPlatformAdmin) {
    return [
      ...baseFilters,
      createDateFilterConfig({
        key: PropertyKey.BUSINESS_UNIT_CREATED,
        propertyLabel: 'Created At',
        groupValuesLabel: 'Date',
        group: 'BusinessUnit',
      }),
      createDateFilterConfig({
        key: PropertyKey.BUSINESS_UNIT_LAST_UPDATED,
        propertyLabel: 'Updated At',
        groupValuesLabel: 'Date',
        group: 'BusinessUnit',
      }),
      {
        key: PropertyKey.BUSINESS_UNIT_ACTIVE_STATUS,
        propertyLabel: 'Active Status',
        operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
        group: 'BusinessUnit',
      },
    ];
  }

  return baseFilters;
};

export const exportBUConfig: ExportFileConfig = {
  fileName: 'bu_list.csv',
  mappings: {
    'Business Unit': (item: ExportCSVBUItem) => item.buName || '',
    'Business Unit Description': (item) => item.buDescription || '',
    'Business Unit Status': (item) => item.buStatus || '',
    'Business Unit Created By': (item) => item.buCreatedBy || '',
    'Business Unit Created At': (item) => item.buCreatedAt || '',
    'Business Unit Updated By': (item) => item.buUpdatedBy || '',
    'Business Unit Updated At': (item) => item.buUpdatedAt || '',
    '': '',
    'Division': (item) => item.dvName || '',
    'Division Description': (item) => item.dvDescription || '',
    'Division Status': (item) => item.dvStatus || '',
    'Division Created By': (item) => item.dvCreatedBy || '',
    'Division Created At': (item) => item.dvCreatedAt || '',
    'Division Updated By': (item) => item.dvUpdatedBy || '',
    'Division Updated At': (item) => item.dvUpdatedAt || '',
  },
};
interface ExportCSVBUItem {
  buName: string;
  buDescription: string;
  buStatus: string;
  buCreatedBy: string;
  buCreatedAt: string;
  buUpdatedBy: string;
  buUpdatedAt: string;
  dvName: string;
  dvDescription: string;
  dvStatus: string;
  dvCreatedBy: string;
  dvCreatedAt: string;
  dvUpdatedBy: string;
  dvUpdatedAt: string;
}
export const convertToExportList = (list?: BusinessUnitWithDivisions[]) => {
  return flatMap(list || [], (item) => {
    const baseData = {
      buName: item.businessUnitId,
      buDescription: item.name,
      buStatus: item.isActive ? 'Active' : 'Inactive',
      buCreatedBy: item.createdBy,
      buCreatedAt: formatDateTime(item.createdAt),
      buUpdatedBy: item.lastUpdatedBy,
      buUpdatedAt: formatDateTime(item.lastUpdatedAt),
    };
    return !item.divisions || item.divisions.length === 0
      ? {
          ...baseData,
          dvName: '',
          dvDescription: '',
          dvStatus: '',
          dvCreatedBy: '',
          dvCreatedAt: '',
          dvUpdatedBy: '',
          dvUpdatedAt: '',
        }
      : item.divisions.map((dv) => ({
          ...baseData,
          dvName: dv.divisionName,
          dvDescription: dv.desc,
          dvStatus: dv.isActive ? 'Active' : 'Inactive',
          dvCreatedBy: dv.createdBy,
          dvCreatedAt: formatDateTime(dv.createdAt),
          dvUpdatedBy: dv.lastUpdatedBy,
          dvUpdatedAt: formatDateTime(dv.lastUpdatedAt),
        }));
  });
};
